import React, { useEffect, useRef, useState } from "react";
import Immutable from "immutable";
import { Button, Flyout, Text, TextArea } from "@showitinc/showit-wc";
import cx from "classnames";
import { deleteProp } from "../../../../utils/PropUpdateUtils";
import Input from "../../../base/Input";
import CustomizeEffects from "../../shared/CustomizeEffects";
import AppConstants from "../../../../constants/AppConstants";
import Icons from "../../../../constants/Icons";
import InputSanitizer from "../../../../utils/InputSanitizer";
import { elementSyncUtils } from "../../../../utils/SyncUtils";
import { ICON_COLOR } from "../../../../constants/MergeConstants";
import SplitMergeWrapper from "../../../base/SplitMergeWrapper";
import { gatherProps } from "@/utils/Merge/MergeUtils.mjs";
import { initFormatter } from "../../../../utils/PropertyFormatters";
import styles from "./IconProperties.module.css";
import {
  BIG_ICONS,
  CUSTOM_ICON,
  CUSTOM_ICON_CONTENT,
  ICON_CONTENT,
} from "../../../../constants/IconConstants";
import IconChooserProperties from "./IconChooserProperties";
import { COLOR_PICKER } from "../../../../constants/InputConstants.mjs";
import { prepareInputUtils } from "../../../../utils/InputUtils";
import { MIXED } from "@/constants/DesignConstants.mjs";

const { ICON_MAX_LENGTH } = AppConstants;
const ALERT_MESSAGE = `This icons exceeds the maximum number of characters allowed (${ICON_MAX_LENGTH}). Please upload as an svg file`;

const cleanupSVG = (inputSVG) => {
  let outSVG = "";
  let val = inputSVG.toLowerCase();
  if (val.indexOf("script") < 0) {
    val = inputSVG;
    const svgRegx = /\<svg[\s\S]*?\<\/svg\>/gi;
    const svgs = svgRegx.exec(val);
    if (svgs != null && svgs[0]) {
      [val] = svgs;
      val = InputSanitizer.sanitizeSVG(val);

      // validate open and closed tags
      const d = document.createElement("div");
      d.innerHTML = val;
      outSVG = d.innerHTML;
    }
  }
  return outSVG;
};

const IconPreview = ({ element, icon, onClick, isLocked }) => {
  const previewRef = useRef();
  if (icon) {
    const svg = {
      shape: "",
      name: "",
      id: "",
    };

    if (icon === CUSTOM_ICON) {
      svg.shape = element.getIn(CUSTOM_ICON_CONTENT.split("."));
    } else {
      const iconObj = Icons.ICON_LIST.find((current) => current.id === icon);
      if (iconObj) {
        svg.shape = iconObj.svg;
        svg.name = iconObj?.name || "";
        svg.id = iconObj.id;
      }
    }

    return (
      <div className={styles["icon-panel-preview"]}>
        <div
          className={cx([
            styles["icon-preview"],
            {
              [styles.big]: icon !== CUSTOM_ICON && BIG_ICONS.includes(svg?.id),
            },
            { [styles.disabled]: isLocked },
          ])}
          dangerouslySetInnerHTML={{ __html: svg.shape }}
          onClick={() => !isLocked && onClick(previewRef.current)}
          ref={previewRef}
        />
        <Button
          lowercase
          variant="outline"
          size="sm"
          onSIClick={() => onClick(previewRef.current)}
          disabled={isLocked}
        >
          Swap Icon
        </Button>
      </div>
    );
  }
  return null;
};

const ElementPropertiesIcon = ({
  element = Immutable.fromJS({}),
  mappedElement,
  onPropUpdate,
  editContent = true,
  layout,
  isLocked,
  preparedElement,
}) => {
  const icon = element.getIn(ICON_CONTENT.split("."), false);
  const hasCustom = icon === CUSTOM_ICON;
  const customSVG = element.getIn(CUSTOM_ICON_CONTENT.split("."), "");
  const [customOpen, setCustomOpen] = useState(icon === CUSTOM_ICON);
  const [invalidIcon, setInvalidIcon] = useState(false);
  const [svgString, setSVGString] = useState(customSVG);
  const flyoutRef = useRef();

  useEffect(() => {
    // clean up corrupted data - non-custom icons shouldn't have customSVG data
    const isNonCustomIcon = icon !== CUSTOM_ICON && icon !== MIXED;
    const hasCustomSVGData = customSVG && customSVG !== MIXED;

    if (isNonCustomIcon && hasCustomSVGData) {
      onPropUpdate(deleteProp(CUSTOM_ICON_CONTENT));
    }
  }, []);

  const openCustomPanel = () => {
    setCustomOpen(true);
  };

  const _onCustomChange = (propInfo) => {
    const svg = cleanupSVG(propInfo.value);

    if (svg.length > ICON_MAX_LENGTH) {
      // eslint-disable-next-line no-alert
      alert(ALERT_MESSAGE);
      setInvalidIcon(true);
    } else if (svg) {
      const updates = [];

      if (!hasCustom) {
        updates.push({ name: ICON_CONTENT, value: CUSTOM_ICON });
      }

      updates.push({ ...propInfo, value: svg });

      onPropUpdate(updates);
      setInvalidIcon(false);
    } else {
      setInvalidIcon(true);
    }
  };

  const { isSynced, toggleSyncedProp, getPropName } = elementSyncUtils(
    element,
    onPropUpdate,
    layout
  );

  const elemProps = gatherProps(preparedElement, layout, [ICON_COLOR], true);
  const separatePath = (path) => path.split(".");
  const getValue = (path) => elemProps.getIn(separatePath(path));
  const prepChange = (path) => [path, getValue(path)];

  const { onChange } = initFormatter(onPropUpdate, getPropName);

  const { canMerge, prepareValue, isSyncMixed } = prepareInputUtils(
    layout,
    preparedElement,
    elemProps
  );

  useEffect(() => {
    setCustomOpen(hasCustom);
  }, [hasCustom]);

  useEffect(() => {
    if (svgString && svgString !== customSVG) {
      _onCustomChange({ name: CUSTOM_ICON_CONTENT, value: svgString });
    }
  }, [svgString]);

  useEffect(() => {
    if (icon !== CUSTOM_ICON) {
      setSVGString("");
      setCustomOpen(false);
    }
  }, [icon]);

  return (
    <>
      {editContent && (
        <>
          <IconPreview
            element={element}
            icon={icon}
            onClick={(target) => {
              flyoutRef.current.showPopover(target);
            }}
            isLocked={isLocked}
          />
          <Flyout
            className={styles["icon-menu"]}
            title="Icon Chooser"
            ref={flyoutRef}
            position="bottom"
            flip={false}
          >
            <IconChooserProperties
              element={element}
              isLocked={isLocked}
              layout={layout}
              onPropUpdate={onPropUpdate}
            />
          </Flyout>
        </>
      )}
      <SplitMergeWrapper
        layout={layout}
        mixed={isSyncMixed(ICON_COLOR)}
        sync={isSynced(ICON_COLOR)}
        toggleSync={() => toggleSyncedProp(ICON_COLOR)}
      >
        <Input
          color={prepareValue(ICON_COLOR)}
          context={canMerge(ICON_COLOR)}
          disabled={isLocked}
          inputType={COLOR_PICKER}
          label="Color"
          onSIBeforeChange={onChange(...prepChange(ICON_COLOR))}
          onSIChange={onChange(...prepChange(ICON_COLOR))}
          onSIInput={onChange(...prepChange(ICON_COLOR), false)}
        />
      </SplitMergeWrapper>
      {editContent && (
        <>
          <TextArea
            className={cx([
              styles["custom-input"],
              { [styles.hide]: !customOpen },
              { [styles.error]: invalidIcon },
            ])}
            onSIInput={(e) => setSVGString(e.detail.value)}
            value={svgString}
            resize
            disabled={isLocked}
          >
            <div className={styles["custom-label"]} slot="label">
              <Text>Custom SVG Icon</Text>
              <Button
                className={styles["add-custom"]}
                icon-prefix="add"
                onSIClick={openCustomPanel}
                icon-size="20"
                variant="icon"
                disabled={isLocked}
              />
            </div>
          </TextArea>
          <Text className={styles["help-link"]}>
            <a
              href="http://learn.showit.co/building-your-site/design/custom-icons"
              target="_blank"
              rel="noreferrer"
            >
              How to create custom icons
            </a>
          </Text>
        </>
      )}
      <CustomizeEffects
        mappedElement={mappedElement}
        layout={layout}
        onPropUpdate={onPropUpdate}
        preparedElement={preparedElement}
      />
    </>
  );
};

export default ElementPropertiesIcon;
